import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  MenuItem,
  TextField,
  Button,
  TableContainer,
  TableHead,
  TableCell,
  TableBody,
  TableRow,
  Table,
  Paper,
} from '@mui/material';
import useStore from 'src/store/cns';
import { useNavigate } from 'react-router-dom';
import CustomAlert from 'src/components/widget/CustomAlert';
import SignTransaction from 'src/components/widget/SignTransaction';
import Loading from 'src/components/widget/Loading';
import { handleError } from 'src/utils/errors/common';
import { mintToken, balanceOf } from 'src/utils/contracts/token';

const MockTokens: React.FC = () => {
  const navigate = useNavigate();
  const tokenList = useStore(state => state.tokens);

  const [tokenAmount, setTokenAmount] = useState<string[][]>([]);
  const [selectedToken, setSelectedToken] = useState<Token | null>(null);
  const [amount, setAmount] = useState<string>('');
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleTokenChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const tokenAddress = event.target.value;
    const token = tokenList.find(t => t.tokenAddress === tokenAddress) || null;
    setSelectedToken(token);
  };

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAmount(event.target.value);
  };

  const handleMint = async () => {
    if (!selectedToken) return;

    try {
      await mintToken(selectedToken.tokenAddress, parseInt(amount));

      setTokenAmount([]);
      const newAmounts = [];
      for (const index in tokenList) {
        if (
          tokenList[index].tokenAddress !==
          '0x0000000000000000000000000000000000000000'
        ) {
          const amount = await balanceOf(tokenList[index].tokenAddress);
          newAmounts.push([
            tokenList[index].name,
            amount.slice(0, amount.length - 18),
          ]);
        }
      }
      setTokenAmount(newAmounts);
    } catch (error) {
      handleError(error, `Minting failed`);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  useEffect(() => {
    const fetchData = async () => {
      setTokenAmount([]);
      const newAmounts = [];
      for (const index in tokenList) {
        if (
          tokenList[index].tokenAddress !==
          '0x0000000000000000000000000000000000000000'
        ) {
          const amount = await balanceOf(tokenList[index].tokenAddress);
          newAmounts.push([
            tokenList[index].name,
            amount.slice(0, amount.length - 18),
          ]);
        }
      }
      setTokenAmount(newAmounts);
    };
    fetchData();
  }, [tokenList]);

  return (
    <Container maxWidth="sm" style={{ marginTop: '50px' }}>
      <Button onClick={() => navigate('/cns')} sx={{ color: 'text.primary' }}>
        Return
      </Button>
      <Typography variant="h4" align="center" gutterBottom>
        Mint ERC-20 Tokens
      </Typography>
      <TextField
        select
        label="Select Token"
        fullWidth
        value={selectedToken?.tokenAddress || ''}
        onChange={handleTokenChange}
        margin="normal"
        variant="outlined"
      >
        {tokenList.slice(1).map(token => (
          <MenuItem key={token.tokenAddress} value={token.tokenAddress}>
            {token.name} ({token.symbol})
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Amount"
        fullWidth
        value={amount}
        onChange={handleAmountChange}
        margin="normal"
        variant="outlined"
        type="number"
      />
      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleMint}
        disabled={!selectedToken || !amount}
        style={{ marginTop: '20px' }}
        sx={{
          color: 'text.primary',
          backgroundColor: 'primary.main',
          padding: '10px 16px',
        }}
      >
        Mint Tokens
      </Button>

      <TableContainer component={Paper} style={{ marginTop: '30px' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Token Name</TableCell>
              <TableCell align="right">Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tokenAmount.map((row, index) => (
              <TableRow key={index}>
                <TableCell component="th" scope="row">
                  {row[0]}
                </TableCell>
                <TableCell align="right">{row[1]}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomAlert
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleCloseSnackbar}
      />
      <SignTransaction />
      <Loading />
    </Container>
  );
};

export default MockTokens;
